import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../lib/api';

const ImportModal = ({ onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importOptions, setImportOptions] = useState({
    skipDuplicates: true,
    updateExisting: false
  });
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setValidationResult(null);
      setImportResult(null);
      setError(null);
    }
  };

  const handleValidate = async () => {
    if (!file) {
      setError('Please select a CSV file first');
      return;
    }

    setError(null);
    setImporting(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API_BASE_URL}/import/validate`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true
        }
      );

      setValidationResult(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to validate CSV file');
    } finally {
      setImporting(false);
    }
  };

  const handleImport = async () => {
    if (!file) {
      setError('Please select a CSV file first');
      return;
    }

    setError(null);
    setImporting(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('options', JSON.stringify(importOptions));

      const response = await axios.post(
        `${API_BASE_URL}/import/execute`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true
        }
      );

      setImportResult(response.data);

      if (response.data.success) {
        // Wait 2 seconds before closing to show success message
        setTimeout(() => {
          onSuccess();
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to import CSV file');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    window.open(`${API_BASE_URL}/import/template`, '_blank');
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content import-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Import Holdings from CSV</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {/* Instructions */}
          <div style={{
            padding: '12px',
            marginBottom: '16px',
            backgroundColor: '#e0f2fe',
            borderLeft: '3px solid #0284c7',
            borderRadius: '4px'
          }}>
            <p style={{ margin: '0 0 8px 0', fontWeight: '500' }}>Instructions:</p>
            <ol style={{ margin: 0, paddingLeft: '20px', fontSize: '0.9rem' }}>
              <li>Download the CSV template to see the required format</li>
              <li>Fill in your holdings data in the CSV file</li>
              <li>Upload the file and click "Validate" to check for errors</li>
              <li>Review the preview and click "Import" to add holdings</li>
            </ol>
          </div>

          {/* Download Template Button */}
          <button
            onClick={handleDownloadTemplate}
            className="download-template-btn"
            style={{
              marginBottom: '16px',
              padding: '8px 16px',
              backgroundColor: '#0284c7',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            📥 Download CSV Template
          </button>

          {/* File Upload */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              Select CSV File:
            </label>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              style={{
                display: 'block',
                padding: '8px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                width: '100%'
              }}
            />
            {file && (
              <p style={{ marginTop: '4px', fontSize: '0.85rem', color: '#6b7280' }}>
                Selected: {file.name} ({(file.size / 1024).toFixed(2)} KB)
              </p>
            )}
          </div>

          {/* Import Options */}
          {file && !importResult && (
            <div style={{
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: '#f9fafb',
              borderRadius: '6px',
              border: '1px solid #e5e7eb'
            }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: '0.9rem' }}>Import Options:</h4>
              <label style={{ display: 'block', marginBottom: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={importOptions.skipDuplicates}
                  onChange={(e) => setImportOptions(prev => ({
                    ...prev,
                    skipDuplicates: e.target.checked,
                    updateExisting: e.target.checked ? prev.updateExisting : false
                  }))}
                  style={{ marginRight: '8px' }}
                />
                <span style={{ fontSize: '0.9rem' }}>Skip duplicates (same name + ticker + platform)</span>
              </label>
              <label style={{
                display: 'block',
                cursor: 'pointer',
                opacity: importOptions.skipDuplicates ? 1 : 0.5
              }}>
                <input
                  type="checkbox"
                  checked={importOptions.updateExisting}
                  onChange={(e) => setImportOptions(prev => ({
                    ...prev,
                    updateExisting: e.target.checked
                  }))}
                  disabled={!importOptions.skipDuplicates}
                  style={{ marginRight: '8px' }}
                />
                <span style={{ fontSize: '0.9rem' }}>Update existing holdings (instead of skipping)</span>
              </label>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div style={{
              padding: '12px',
              marginBottom: '16px',
              backgroundColor: '#fee2e2',
              borderLeft: '3px solid #ef4444',
              borderRadius: '4px',
              color: '#991b1b'
            }}>
              {error}
            </div>
          )}

          {/* Validation Result */}
          {validationResult && (
            <div style={{
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: validationResult.success ? '#d1fae5' : '#fee2e2',
              borderLeft: `3px solid ${validationResult.success ? '#10b981' : '#ef4444'}`,
              borderRadius: '4px'
            }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '1rem' }}>
                {validationResult.success ? '✓ Validation Passed' : '✗ Validation Failed'}
              </h4>
              <p style={{ margin: '0 0 4px 0', fontSize: '0.9rem' }}>
                Total rows: {validationResult.totalRows}
              </p>
              <p style={{ margin: '0 0 4px 0', fontSize: '0.9rem' }}>
                Valid rows: {validationResult.validRows}
              </p>

              {validationResult.errors && validationResult.errors.length > 0 && (
                <div style={{ marginTop: '12px' }}>
                  <h5 style={{ margin: '0 0 8px 0', fontSize: '0.9rem', color: '#991b1b' }}>
                    Errors ({validationResult.errors.length}):
                  </h5>
                  <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.85rem', maxHeight: '150px', overflowY: 'auto' }}>
                    {validationResult.errors.map((err, index) => (
                      <li key={index} style={{ marginBottom: '4px', color: '#991b1b' }}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}

              {validationResult.preview && validationResult.preview.length > 0 && (
                <div style={{ marginTop: '12px' }}>
                  <h5 style={{ margin: '0 0 8px 0', fontSize: '0.9rem' }}>
                    Preview (first {validationResult.preview.length} rows):
                  </h5>
                  <div style={{ maxHeight: '200px', overflowY: 'auto', fontSize: '0.85rem' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f3f4f6' }}>
                          <th style={{ padding: '6px', border: '1px solid #d1d5db', textAlign: 'left' }}>Asset Name</th>
                          <th style={{ padding: '6px', border: '1px solid #d1d5db', textAlign: 'left' }}>Type</th>
                          <th style={{ padding: '6px', border: '1px solid #d1d5db', textAlign: 'left' }}>Ticker</th>
                          <th style={{ padding: '6px', border: '1px solid #d1d5db', textAlign: 'right' }}>Units</th>
                        </tr>
                      </thead>
                      <tbody>
                        {validationResult.preview.map((row, index) => (
                          <tr key={index}>
                            <td style={{ padding: '6px', border: '1px solid #d1d5db' }}>{row.assetName}</td>
                            <td style={{ padding: '6px', border: '1px solid #d1d5db' }}>{row.assetType}</td>
                            <td style={{ padding: '6px', border: '1px solid #d1d5db' }}>{row.ticker || '-'}</td>
                            <td style={{ padding: '6px', border: '1px solid #d1d5db', textAlign: 'right' }}>{row.units}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Import Result */}
          {importResult && (
            <div style={{
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: importResult.success ? '#d1fae5' : '#fee2e2',
              borderLeft: `3px solid ${importResult.success ? '#10b981' : '#ef4444'}`,
              borderRadius: '4px'
            }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '1rem' }}>
                {importResult.success ? '✓ Import Completed' : '✗ Import Failed'}
              </h4>
              {importResult.created > 0 && (
                <p style={{ margin: '0 0 4px 0', fontSize: '0.9rem', color: '#065f46' }}>
                  ✓ Created: {importResult.created} holdings
                </p>
              )}
              {importResult.updated > 0 && (
                <p style={{ margin: '0 0 4px 0', fontSize: '0.9rem', color: '#065f46' }}>
                  ✓ Updated: {importResult.updated} holdings
                </p>
              )}
              {importResult.skipped > 0 && (
                <p style={{ margin: '0 0 4px 0', fontSize: '0.9rem', color: '#92400e' }}>
                  ⊘ Skipped: {importResult.skipped} duplicates
                </p>
              )}

              {importResult.errors && importResult.errors.length > 0 && (
                <div style={{ marginTop: '12px' }}>
                  <h5 style={{ margin: '0 0 8px 0', fontSize: '0.9rem', color: '#991b1b' }}>
                    Errors ({importResult.errors.length}):
                  </h5>
                  <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.85rem', maxHeight: '150px', overflowY: 'auto' }}>
                    {importResult.errors.map((err, index) => (
                      <li key={index} style={{ marginBottom: '4px', color: '#991b1b' }}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="cancel-btn">
            {importResult?.success ? 'Close' : 'Cancel'}
          </button>
          {!importResult && (
            <>
              <button
                onClick={handleValidate}
                disabled={!file || importing}
                className="validate-btn"
                style={{
                  padding: '8px 16px',
                  backgroundColor: !file || importing ? '#9ca3af' : '#0284c7',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: !file || importing ? 'not-allowed' : 'pointer'
                }}
              >
                {importing ? 'Validating...' : 'Validate'}
              </button>
              <button
                onClick={handleImport}
                disabled={!validationResult || !validationResult.success || importing}
                className="submit-btn"
                style={{
                  padding: '8px 16px',
                  backgroundColor: !validationResult || !validationResult.success || importing ? '#9ca3af' : '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: !validationResult || !validationResult.success || importing ? 'not-allowed' : 'pointer'
                }}
              >
                {importing ? 'Importing...' : 'Import'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportModal;
